import React, { useState } from 'react';
import { Calendar, MapPin, X, ChevronLeft, ChevronRight } from 'lucide-react';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Projekt-Daten - Hier kannst du einfach neue Projekte hinzufügen
  const projects = [
    {
      id: 1,
      title: "Fotografie für den USV Eschen Mauren",
      description: "Medien, Kommunikation, IT und Vereinsfotografie für den USV Eschen Mauren - Dokumentation von Spielen, und besondere Momente auf dem Platz.",
      date: "2026",
      location: "Eschen-Mauren, Liechtenstein",
      category: "Sportfotografie",
      coverImage: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800",
      images: [
        {
          url: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=1200",
          caption: ""
        }
      ]
    }
    // Hier weitere Projekte hinzufügen:
    // {
    //   id: 2,
    //   title: "Dein nächstes Projekt",
    //   description: "Beschreibung...",
    //   date: "2024",
    //   location: "Ort",
    //   category: "Kategorie",
    //   coverImage: "URL zum Cover-Bild",
    //   images: [
    //     { url: "Bild URL", caption: "Beschreibung" }
    //   ]
    // }
  ];

  const openProject = (project) => {
    setSelectedProject(project);
    setSelectedImageIndex(0);
  };

  const closeProject = () => {
    setSelectedProject(null);
    setSelectedImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject) {
      setSelectedImageIndex((prev) => 
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <section id="projects" className="py-20 px-4 bg-gradient-to-b from-black to-[#0a0a0a]">
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <div className="mb-16 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Projekte
          </h2>
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-6" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Fotografie-Projekte und Aufträge
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <div
              key={project.id}
              onClick={() => openProject(project)}
              className="group cursor-pointer bg-gray-900/50 rounded-lg overflow-hidden border border-gray-800 hover:border-amber-500 transition-all duration-300 hover:transform hover:scale-[1.02]"
            >
              {/* Cover Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={project.coverImage}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-4 right-4 bg-amber-500 text-black px-3 py-1 rounded-full text-sm font-semibold">
                  {project.category}
                </div>
              </div>

              {/* Project Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {project.description}
                </p>
                <div className="flex items-center gap-4 text-gray-500 text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar size={16} className="text-amber-500" />
                    <span>{project.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin size={16} className="text-amber-500" />
                    <span>{project.location}</span>
                  </div>
                </div>
                <div className="mt-4 text-amber-500 text-sm font-semibold group-hover:text-amber-400">
                  {project.images.length} Fotos anzeigen →
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State wenn keine Projekte */}
        {projects.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">Noch keine Projekte vorhanden.</p>
          </div>
        )}
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          {/* Close Button */}
          <button
            onClick={closeProject}
            className="absolute top-6 right-6 text-white hover:text-amber-400 transition-colors z-10"
          >
            <X size={32} />
          </button>

          <div className="max-w-6xl w-full">
            {/* Project Header */}
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {selectedProject.title}
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto mb-4">
                {selectedProject.description}
              </p>
              <div className="flex items-center justify-center gap-6 text-gray-500 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar size={18} className="text-amber-500" />
                  <span>{selectedProject.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin size={18} className="text-amber-500" />
                  <span>{selectedProject.location}</span>
                </div>
              </div>
            </div>

            {/* Image Gallery with Navigation */}
            <div className="relative">
              {/* Main Image */}
              <div className="relative aspect-[16/10] max-h-[70vh] bg-black rounded-lg overflow-hidden">
                <img
                  src={selectedProject.images[selectedImageIndex].url}
                  alt={selectedProject.images[selectedImageIndex].caption}
                  className="w-full h-full object-contain"
                />
                
                {/* Image Caption */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                  <p className="text-white text-center">
                    {selectedProject.images[selectedImageIndex].caption}
                  </p>
                  <p className="text-gray-400 text-sm text-center mt-2">
                    Bild {selectedImageIndex + 1} von {selectedProject.images.length}
                  </p>
                </div>

                {/* Navigation Arrows */}
                {selectedProject.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-amber-500 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-amber-500 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Navigation */}
              {selectedProject.images.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                  {selectedProject.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        index === selectedImageIndex
                          ? 'border-amber-500 scale-105'
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
